<template>
  <div class="ai-query-root">
    <!-- 悬浮入口：Siri 风格呼吸球，可拖动 -->
    <div
      class="ai-fab"
      ref="fab"
      :class="{ 'is-open': visible, 'is-dragging': dragging }"
      :style="fabStyle"
      title="AI 查询（可拖动调整位置）"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="onFabClick"
    >
      <span class="ai-fab-orb"></span>
    </div>

    <el-drawer
      ref="aiDrawer"
      title="AI 查询"
      :visible.sync="visible"
      direction="rtl"
      size="434px"
      :modal="false"
      custom-class="ai-drawer"
      @opened="onDrawerOpened"
      @closed="onClosed"
    >
      <div class="ai-panel">
        <!-- 对话区 -->
        <div class="ai-body" ref="body">
          <div v-if="!messages.length" class="ai-empty">
            <div class="ai-empty-head">
              <i class="el-icon-chat-dot-round"></i>
              <div>
                <p class="ai-empty-title">用大白话提问，我来查</p>
              </div>
            </div>
            <p class="ai-empty-label">试试这样问：</p>
            <div class="ai-samples">
              <div
                v-for="(sample, idx) in samples"
                :key="idx"
                class="ai-sample"
                @click="ask(sample)"
              >
                <i class="el-icon-search"></i>
                <span>{{ sample }}</span>
              </div>
            </div>
          </div>

          <div v-for="(msg, idx) in messages" :key="idx" class="ai-msg" :class="'is-' + msg.role">
            <div class="ai-bubble">
              <div class="ai-text" v-if="msg.text">{{ msg.text }}</div>

              <!-- 图表 -->
              <div v-if="msg.chart" class="ai-chart" :ref="'chart' + idx"></div>

              <!-- 结果表 -->
              <div v-if="msg.rows && msg.rows.length" class="ai-table-wrap">
                <el-table :data="msg.rows" size="mini" border max-height="260" style="width: 100%">
                  <el-table-column
                    v-for="(label, field) in msg.columns"
                    :key="field"
                    :prop="field"
                    :label="label"
                    show-overflow-tooltip
                  />
                </el-table>
                <div class="ai-table-bar">
                  <span class="ai-count">共 {{ msg.count }} 条</span>
                  <el-button type="text" size="mini" @click="exportResult(msg)">导出</el-button>
                  <el-button type="text" size="mini" @click="saveCurrent(msg)">保存为常用</el-button>
                  <el-button type="text" size="mini" @click="subscribeCurrent(msg)">订阅</el-button>
                </div>
              </div>

              <div v-if="msg.error" class="ai-error">{{ msg.error }}</div>
            </div>
          </div>

          <div v-if="loading" class="ai-msg is-assistant">
            <div class="ai-bubble"><i class="el-icon-loading"></i> 正在查询…</div>
          </div>
        </div>

        <!-- 输入区 -->
        <div class="ai-input">
          <el-input
            v-model="question"
            type="textarea"
            :rows="2"
            resize="none"
            placeholder="用大白话问，例如：木工班组上月缺勤超过5天的人"
            @keydown.native.enter.exact.prevent="ask()"
          />
          <div class="ai-actions">
            <el-button v-if="speechSupported" size="mini" icon="el-icon-microphone" :type="recording ? 'danger' : 'default'" @click="toggleVoice">{{ recording ? '停止' : '语音' }}</el-button>
            <el-button size="mini" icon="el-icon-data-analysis" @click="compareAsk">对比</el-button>
            <el-button size="mini" icon="el-icon-refresh" @click="clearContext">清空</el-button>
            <el-button size="mini" icon="el-icon-star-off" @click="openSaved">常用/订阅</el-button>
            <div class="ai-actions-right">
              <el-button size="mini" type="primary" :loading="loading" @click="ask()">查询</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>

    <!-- 常用查询 / 订阅 -->
    <el-dialog
      ref="savedDialog"
      title="常用查询与订阅"
      :visible.sync="savedVisible"
      width="560px"
      :modal="false"
      custom-class="ai-saved-dialog"
      @opened="onDialogOpened"
    >
      <el-tabs v-model="savedTab">
        <el-tab-pane label="常用查询" name="saved">
          <div v-if="!savedList.length" class="ai-tip">还没有保存的常用查询</div>
          <div v-for="item in savedList" :key="item.id" class="ai-saved-item">
            <div class="ai-saved-main">
              <div class="ai-saved-name">{{ item.name }}</div>
              <div class="ai-saved-q">{{ item.question }}</div>
            </div>
            <div class="ai-saved-ops">
              <el-button type="text" size="mini" @click="runSaved(item)">执行</el-button>
              <el-button type="text" size="mini" @click="removeSaved(item)">删除</el-button>
            </div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="我的订阅" name="sub">
          <div v-if="!subList.length" class="ai-tip">还没有订阅</div>
          <div v-for="item in subList" :key="item.id" class="ai-saved-item">
            <div class="ai-saved-main">
              <div class="ai-saved-name">{{ item.name }}</div>
              <div class="ai-saved-q">
                {{ item.frequency === 'weekly' ? '每周' : '每天' }} {{ item.push_time }}
                <span v-if="!Number(item.enabled)" class="ai-off">已停用</span>
              </div>
            </div>
            <div class="ai-saved-ops">
              <el-button type="text" size="mini" @click="cancelSubscribe(item)">取消</el-button>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <!-- 两组对比：一次填两组条件 -->
    <el-dialog
      ref="compareDialog"
      title="两组对比"
      :visible.sync="compareVisible"
      width="480px"
      :modal="false"
      custom-class="ai-compare-dialog"
      @opened="onCompareOpened"
    >
      <div class="ai-compare-row">
        <div class="ai-compare-field">
          <div class="ai-compare-label">第一组</div>
          <el-input v-model="compareLeft" size="small" placeholder="例如：木工班组" @keyup.enter.native="doCompare" />
        </div>
        <div class="ai-compare-vs">vs</div>
        <div class="ai-compare-field">
          <div class="ai-compare-label">第二组</div>
          <el-input v-model="compareRight" size="small" placeholder="例如：钢筋班组" @keyup.enter.native="doCompare" />
        </div>
      </div>
      <div slot="footer">
        <el-button size="mini" @click="compareVisible = false">取 消</el-button>
        <el-button size="mini" type="primary" :loading="loading" @click="doCompare">开始对比</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  aiAsk,
  aiCompare,
  aiExport,
  aiSavedSave,
  aiSavedLists,
  aiSavedRemove,
  aiSavedRun,
  aiSubscribe,
  aiSubscribeLists,
  aiSubscribeCancel,
  aiClearConversation
} from '@/api/ai'

export default {
  name: 'AiQuery',
  data() {
    return {
      // 层级常量：必须用内联样式下发，原因见 setWrapperZIndex 注释
      FAB_Z: 2147481000,
      DRAWER_Z: 2147482000,
      DIALOG_Z: 2147482100,
      // 抽屉顶部让出页面标题栏（设计稿 1920 宽下的像素，运行时会按视口等比换算）
      DRAWER_TOP_DESIGN: 118,
      FAB_SIZE: 58,
      // 悬浮球位置（拖动后写入；null 表示跟随导出二维码，找不到时用右下角）
      fabPos: null,
      // 是否已使用用户拖动保存的位置
      fabPosLocked: false,
      fabPositionObserver: null,
      fabPositionFrame: null,
      dragging: false,
      // 按下时的拖动上下文（不放进 data 响应式，避免无谓渲染）
      dragCtx: null,
      justDragged: false,
      justDraggedTimer: null,
      visible: false,
      savedVisible: false,
      savedTab: 'saved',
      compareVisible: false,
      compareLeft: '',
      compareRight: '',
      question: '',
      loading: false,
      recording: false,
      speechSupported: false,
      messages: [],
      savedList: [],
      subList: [],
      samples: [
        // 人员档案
        '超过60岁的工人有哪些',
        '身份证三个月内到期的人',
        '本月新进场了多少人',
        // 考勤
        '木工班组上个月缺勤超过5天的人',
        '各班组今天出勤人数',
        // 进退场
        '今天进场和退场各多少人',
        '待审核的进退场申请有多少',
        // 班组 / 黑名单
        '各班组人数分布',
        '共有多少个班组',
        '黑名单里有哪些人',
        // 设备
        '项目上有哪些设备',
        '设备在线情况怎么样'
      ],
      // 浏览器原生语音识别
      nativeRecognition: null
    }
  },
  computed: {
    /**
     * 悬浮球定位：拖动过或已锚定到导出二维码时使用 left/top
     */
    fabStyle() {
      const style = { zIndex: this.FAB_Z }
      if (this.fabPos) {
        style.left = this.fabPos.left + 'px'
        style.top = this.fabPos.top + 'px'
        style.right = 'auto'
        style.bottom = 'auto'
      }
      return style
    }
  },
  watch: {
    '$route.path'() {
      this.scheduleDefaultFabPosition()
    }
  },
  mounted() {
    this.speechSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition)
    this.loadFabPos()
    this.$nextTick(() => {
      this.scheduleDefaultFabPosition()
      this.observeFabPosition()
      window.addEventListener('resize', this.scheduleDefaultFabPosition)
    })
  },
  beforeDestroy() {
    this.stopVoice()
    this.unbindDrag()
    window.clearTimeout(this.justDraggedTimer)
    window.cancelAnimationFrame(this.fabPositionFrame)
    window.removeEventListener('resize', this.scheduleDefaultFabPosition)
    if (this.fabPositionObserver) {
      this.fabPositionObserver.disconnect()
      this.fabPositionObserver = null
    }
  },
  methods: {
    observeFabPosition() {
      if (!window.MutationObserver || !document.body) return
      this.fabPositionObserver = new MutationObserver(() => {
        this.scheduleDefaultFabPosition()
      })
      this.fabPositionObserver.observe(document.body, {
        childList: true,
        subtree: true
      })
    },
    scheduleDefaultFabPosition() {
      if (this.fabPosLocked || this.fabPositionFrame) return
      this.fabPositionFrame = window.requestAnimationFrame(() => {
        this.fabPositionFrame = null
        this.positionFabNearQrExport()
      })
    },
    /**
     * 未拖动过时，把悬浮球放到“导出二维码”按钮右侧。
     * 二维码按钮由外部脚本延迟注入，因此这里会持续观察 DOM。
     */
    positionFabNearQrExport() {
      if (this.fabPosLocked) return
      const btn = document.querySelector('.to_export_qr')
      if (!btn) {
        if (this.fabPos) this.fabPos = null
        return
      }

      const fab = this.$refs.fab || this.$el.querySelector('.ai-fab')
      if (!fab) return
      const btnRect = btn.getBoundingClientRect()
      const fabRect = fab.getBoundingClientRect()
      if (!btnRect.width && !btnRect.height) return

      const w = fabRect.width || this.FAB_SIZE
      const h = fabRect.height || this.FAB_SIZE
      const gap = 10
      const maxLeft = Math.max(0, window.innerWidth - w)
      const maxTop = Math.max(0, window.innerHeight - h)
      const left = Math.min(btnRect.right + gap, maxLeft)
      const top = Math.min(
        Math.max(btnRect.top + (btnRect.height - h) / 2, 0),
        maxTop
      )

      this.fabPos = { left, top }
    },
    /**
     * 给 Element UI 抽屉/弹窗的 wrapper 下发内联 z-index。
     *
     * 为什么不用 CSS：
     *   本项目 postcss 链路里的 cssnano 会重排样式表中的 z-index
     *   （实测写在 scoped 样式里的 20000 被压缩成了 2），
     *   而页面自身有 z-index 2500 / 9999 / 10000 的浮层，
     *   结果就是面板被页面按钮遮挡。内联样式不经过 postcss，
     *   数值可靠，且内联优先级高于任何样式表规则。
     *
     * Element UI 的 .el-drawer__wrapper 默认不带 z-index，不会被组件覆盖，
     * 所以这里设置后能稳定生效。
     */
    setWrapperZIndex(refName, zIndex, wrapperClass) {
      this.$nextTick(() => {
        const comp = this.$refs[refName]
        if (!comp) return
        let el = comp.$el || comp
        // $el 通常就是 wrapper，但不同版本可能包一层，兜底向内查找
        if (el && el.classList && wrapperClass && !el.classList.contains(wrapperClass)) {
          const found = el.querySelector('.' + wrapperClass)
          if (found) el = found
        }
        if (el && el.style) {
          el.style.zIndex = String(zIndex)
        }
      })
    },
    onDrawerOpened() {
      this.setWrapperZIndex('aiDrawer', this.DRAWER_Z, 'el-drawer__wrapper')
      this.setDrawerTop()
    },
    /**
     * 让抽屉顶部让出页面标题栏，避免盖住"XX项目"那一行。
     * 优先实测页面标题栏（.header，企业/项目两种头都叫这个类名）的真实底边，
     * 任何屏宽、任何端都不可能高过标题栏；取不到时退回 1920 设计稿等比换算。
     * 用内联样式下发，不经过 postcss。
     */
    setDrawerTop() {
      this.$nextTick(() => {
        const comp = this.$refs.aiDrawer
        const wrapper = comp && comp.$el
        const panel = wrapper && wrapper.querySelector
          ? wrapper.querySelector('.el-drawer')
          : null
        if (!panel) return
        const scale = window.innerWidth / 1920
        let top = Math.round(this.DRAWER_TOP_DESIGN * scale)
        const bar = document.querySelector('div.header')
        if (bar && bar.getBoundingClientRect) {
          const rect = bar.getBoundingClientRect()
          // 底边有效且在屏幕顶部区域内（防止误取到其他叫 .header 的元素）
          if (rect.height > 0 && rect.bottom > 0 && rect.bottom < 400) {
            top = Math.max(top, Math.round(rect.bottom) + 8)
          }
        }
        panel.style.top = top + 'px'
        panel.style.bottom = '0'
        panel.style.height = 'auto'
      })
    },
    onDialogOpened() {
      this.setWrapperZIndex('savedDialog', this.DIALOG_Z, 'el-dialog__wrapper')
    },
    onCompareOpened() {
      this.setWrapperZIndex('compareDialog', this.DIALOG_Z, 'el-dialog__wrapper')
    },

    /* ---------------- 悬浮球拖动 ---------------- */

    /**
     * 按下开始拖动。拖动判定阈值 3px，未超过阈值仍视为点击。
     */
    startDrag(e) {
      const evt = e.touches ? e.touches[0] : e
      const fab = this.$refs.fab || this.$el.querySelector('.ai-fab')
      if (!fab) return
      const rect = fab.getBoundingClientRect()

      this.dragCtx = {
        startX: evt.clientX,
        startY: evt.clientY,
        originLeft: rect.left,
        originTop: rect.top,
        // 实际渲染尺寸（样式里的 px 会被项目编译成 vw 缩放，不能按常量算）
        w: rect.width || this.FAB_SIZE,
        h: rect.height || this.FAB_SIZE,
        moved: false
      }
      this.dragging = true
      this.justDragged = false

      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.stopDrag)
      document.addEventListener('touchmove', this.onDragMove, { passive: false })
      document.addEventListener('touchend', this.stopDrag)
    },
    onDragMove(e) {
      if (!this.dragCtx) return
      const evt = e.touches ? e.touches[0] : e
      const dx = evt.clientX - this.dragCtx.startX
      const dy = evt.clientY - this.dragCtx.startY
      if (!this.dragCtx.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return
      this.dragCtx.moved = true

      // 限制在视口内，避免拖出屏幕找不回来
      const maxLeft = window.innerWidth - this.dragCtx.w
      const maxTop = window.innerHeight - this.dragCtx.h
      const left = Math.min(Math.max(this.dragCtx.originLeft + dx, 0), Math.max(0, maxLeft))
      const top = Math.min(Math.max(this.dragCtx.originTop + dy, 0), Math.max(0, maxTop))
      this.fabPos = { left, top }

      if (e.cancelable) e.preventDefault()
    },
    stopDrag() {
      this.unbindDrag()
      if (this.dragCtx && this.dragCtx.moved) {
        this.justDragged = true
        this.saveFabPos()
        // 短暂屏蔽一次 click，避免拖完误触；必须自动过期，
        // 否则在按钮外松手时不会有 click 来消费它，下次真实点击会被吞掉
        window.clearTimeout(this.justDraggedTimer)
        this.justDraggedTimer = window.setTimeout(() => {
          this.justDragged = false
        }, 300)
      }
      this.dragging = false
      this.dragCtx = null
    },
    unbindDrag() {
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.stopDrag)
      document.removeEventListener('touchmove', this.onDragMove)
      document.removeEventListener('touchend', this.stopDrag)
    },
    onFabClick() {
      // 刚拖动过就不当作点击，避免拖完误触开面板
      if (this.justDragged) {
        this.justDragged = false
        return
      }
      this.toggle()
    },
    saveFabPos() {
      try {
        window.localStorage.setItem('ai_query_fab_pos', JSON.stringify(this.fabPos))
      } catch (e) {
        // 隐私模式下 localStorage 可能不可用，忽略
      }
    },
    loadFabPos() {
      try {
        const raw = window.localStorage.getItem('ai_query_fab_pos')
        if (!raw) return
        const pos = JSON.parse(raw)
        if (pos && typeof pos.left === 'number' && typeof pos.top === 'number') {
          // 换屏或缩放后原坐标可能已在视口外，做个兜底修正
          const fab = this.$refs.fab
          const rect = fab && fab.getBoundingClientRect ? fab.getBoundingClientRect() : null
          const w = (rect && rect.width) || this.FAB_SIZE
          const h = (rect && rect.height) || this.FAB_SIZE
          const maxLeft = Math.max(0, window.innerWidth - w)
          const maxTop = Math.max(0, window.innerHeight - h)
          this.fabPos = {
            left: Math.min(Math.max(pos.left, 0), maxLeft),
            top: Math.min(Math.max(pos.top, 0), maxTop)
          }
          this.fabPosLocked = true
        }
      } catch (e) {
        // 解析失败就用导出二维码旁的默认位置
      }
    },

    toggle() {
      this.visible = !this.visible
      if (this.visible) {
        this.$nextTick(this.scrollToBottom)
      }
    },
    onClosed() {
      this.stopVoice()
    },

    /** 主流程：提问 */
    async ask(text) {
      const question = (text || this.question || '').trim()
      if (!question || this.loading) return

      this.question = ''
      this.messages.push({ role: 'user', text: question })
      this.scrollToBottom()
      this.loading = true

      try {
        const res = await aiAsk(question)
        const data = res.data || {}
        // 全量计数（metric=count 无分组）只有一行 {count}，不需要表格/图表，数字在结论文字里
        const isTotalCount = data.plan && data.plan.metric === 'count' && !data.plan.group_by
        const msg = {
          role: 'assistant',
          text: data.answer || '',
          rows: isTotalCount ? [] : (data.rows || []),
          columns: isTotalCount ? {} : (data.columns || {}),
          count: data.count || 0,
          chart: isTotalCount ? null : (data.chart || null),
          plan: data.plan || null
        }
        const idx = this.messages.push(msg) - 1
        this.$nextTick(() => {
          this.renderChart(idx, msg.chart)
          this.scrollToBottom()
        })
      } catch (e) {
        // request 拦截器已提示错误，这里只落一条占位
        this.messages.push({ role: 'assistant', error: (e && e.data) || '查询失败' })
      } finally {
        this.loading = false
      }
    },

    /** 图表渲染 */
    renderChart(idx, option) {
      if (!option) return
      const el = this.$refs['chart' + idx]
      const dom = Array.isArray(el) ? el[0] : el
      if (!dom) return
      const chart = this.$echarts.init(dom)
      chart.setOption(option)
    },

    scrollToBottom() {
      const body = this.$refs.body
      if (body) body.scrollTop = body.scrollHeight
    },

    /** 清空上下文（后端 Redis 会话） */
    async clearContext() {
      try {
        await aiClearConversation()
        this.messages = []
        this.$message.success('已清空上下文')
      } catch (e) {
        // 拦截器已提示
      }
    },

    /** Q2 两组对比：依次输入两个条件，渲染对比图 */
    /** 对比：一个弹窗同时填两组条件，不再分步输入 */
    compareAsk() {
      if (this.loading) return
      this.compareLeft = ''
      this.compareRight = ''
      this.compareVisible = true
    },

    /** 执行两组对比 */
    async doCompare() {
      if (this.loading) return
      const left = (this.compareLeft || '').trim()
      const right = (this.compareRight || '').trim()
      if (!left || !right) {
        this.$message.warning('请填写两组对比条件')
        return
      }
      this.compareVisible = false
      this.messages.push({ role: 'user', text: '对比：' + left + ' vs ' + right })
      this.loading = true
      try {
        const res = await aiCompare(left, right)
        const data = res.data || {}
        const msg = {
          role: 'assistant',
          text: '对比结果：' + left + ' 共 ' + ((data.left || {}).count || 0)
            + '，' + right + ' 共 ' + ((data.right || {}).count || 0),
          chart: data.chart || null,
          rows: [],
          columns: {},
          count: 0
        }
        const idx = this.messages.push(msg) - 1
        this.$nextTick(() => {
          this.renderChart(idx, msg.chart)
          this.scrollToBottom()
        })
      } catch (e) {
        this.messages.push({ role: 'assistant', error: (e && (e.data || e.message)) || '对比失败' })
      } finally {
        this.loading = false
        this.scrollToBottom()
      }
    },

    /** 导出：文件内容 base64 随接口返回，前端用 Blob 落地成下载（不走 OSS） */
    async exportResult(msg) {
      try {
        const res = await aiExport({ plan: msg.plan, question: '' })
        const data = res.data || {}
        if (!data.content) return
        const bin = atob(data.content)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        const blob = new Blob([bytes], { type: data.mime || 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = data.name || 'AI查询结果.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        // 拦截器已提示
      }
    },

    /** 保存为常用查询 */
    async saveCurrent(msg) {
      try {
        const { value } = await this.$prompt('给这个查询起个名字', '保存为常用查询', {
          inputValue: (msg.plan && msg.plan.answer_hint) || '常用查询'
        })
        await aiSavedSave({ name: value, question: '', plan: msg.plan })
        this.$message.success('已保存')
      } catch (e) {
        // 取消或失败
      }
    },

    /** 订阅 */
    async subscribeCurrent(msg) {
      try {
        const { value } = await this.$prompt('推送频率 daily（每天）/ weekly（每周）', '订阅推送', {
          inputValue: 'daily'
        })
        await aiSubscribe({
          name: (msg.plan && msg.plan.answer_hint) || '订阅查询',
          question: '',
          plan: msg.plan,
          frequency: value === 'weekly' ? 'weekly' : 'daily',
          push_time: '09:00'
        })
        this.$message.success('订阅成功')
      } catch (e) {
        // 取消或失败
      }
    },

    /** 打开常用/订阅 */
    async openSaved() {
      this.savedVisible = true
      try {
        const [saved, sub] = await Promise.all([aiSavedLists(), aiSubscribeLists()])
        this.savedList = (saved.data || {}).list || []
        this.subList = (sub.data || {}).list || []
      } catch (e) {
        // 拦截器已提示
      }
    },

    async runSaved(item) {
      this.savedVisible = false
      this.visible = true
      this.messages.push({ role: 'user', text: item.name })
      this.loading = true
      try {
        const res = await aiSavedRun(item.id)
        const data = res.data || {}
        const msg = {
          role: 'assistant',
          text: data.answer || '',
          rows: data.rows || [],
          columns: data.columns || {},
          count: data.count || 0,
          chart: data.chart || null,
          plan: data.plan || null
        }
        const idx = this.messages.push(msg) - 1
        this.$nextTick(() => {
          this.renderChart(idx, msg.chart)
          this.scrollToBottom()
        })
      } catch (e) {
        this.messages.push({ role: 'assistant', error: (e && e.data) || '执行失败' })
      } finally {
        this.loading = false
      }
    },

    async removeSaved(item) {
      try {
        await aiSavedRemove(item.id)
        this.savedList = this.savedList.filter(i => i.id !== item.id)
        this.$message.success('已删除')
      } catch (e) {
        // 拦截器已提示
      }
    },

    async cancelSubscribe(item) {
      try {
        await aiSubscribeCancel(item.id)
        item.enabled = 0
        this.$message.success('已取消')
      } catch (e) {
        // 拦截器已提示
      }
    },

    /** 语音输入：仅使用浏览器原生识别，不向本项目后端上传录音 */
    toggleVoice() {
      if (this.recording) {
        this.stopVoice()
        return
      }

      const SR = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!SR) {
        this.$message.warning('当前浏览器不支持语音输入，请使用 Chrome 或 Edge')
        return
      }

      try {
        const rec = new SR()
        rec.lang = 'zh-CN'
        rec.continuous = false
        rec.interimResults = false
        rec.maxAlternatives = 1
        rec.onresult = evt => {
          const result = evt.results[0] && evt.results[0][0]
          const text = result ? (result.transcript || '').trim() : ''
          this.recording = false
          this.nativeRecognition = null
          if (text) {
            this.ask(text)
          } else {
            this.$message.warning('没有识别到内容，请重试')
          }
        }
        rec.onerror = evt => {
          this.recording = false
          this.nativeRecognition = null
          this.$message.error(this.voiceErrorMessage(evt))
        }
        rec.onend = () => {
          this.recording = false
          this.nativeRecognition = null
        }
        this.nativeRecognition = rec
        this.recording = true
        this.$message.info('请说话')
        rec.start()
      } catch (e) {
        this.recording = false
        this.nativeRecognition = null
        this.$message.error('语音识别启动失败，请重试')
      }
    },

    voiceErrorMessage(evt) {
      const error = evt && evt.error
      const messages = {
        'not-allowed': '麦克风权限被拒绝，请在浏览器设置中允许访问',
        'service-not-allowed': '浏览器语音识别服务不可用',
        'audio-capture': '未检测到可用的麦克风',
        'no-speech': '没有识别到语音，请重试',
        network: '浏览器语音识别服务连接失败，请检查网络'
      }
      return messages[error] || '语音识别失败，请重试'
    },

    stopVoice() {
      if (!this.recording && !this.nativeRecognition) return
      this.recording = false

      if (this.nativeRecognition) {
        try { this.nativeRecognition.stop() } catch (e) {}
        this.nativeRecognition = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* ------------------------------------------------------------------
   层级（z-index）不在这里写：
   本项目 postcss 链路里的 cssnano 会重排样式表中的 z-index
   （实测 scoped 样式里的 20000 被压缩成 2），而页面自身有
   z-index 2500/9999/10000 的浮层，导致面板被遮挡。
   因此层级统一在 JS 里用内联样式下发，见 setWrapperZIndex()。
   ------------------------------------------------------------------ */
/* 抽屉描边 + 圆角 + 投影：浮层面板质感；同时关闭弹出/退出动画（即显即隐） */
/deep/ .ai-drawer {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.12);
  /* 面板滑入/滑出动画（element: rtl-drawer-in .3s / rtl-drawer-out .3s）；
     打开态选择器 .el-drawer__open .el-drawer.rtl 优先级更高，必须 !important 压过 */
  animation: none !important;
}
/* 抽屉外层 wrapper 的淡入淡出（element: el-drawer-fade .3s），一并关掉 */
/deep/ .el-drawer-fade-enter-active,
/deep/ .el-drawer-fade-leave-active {
  animation: none !important;
}
/* 抽屉头部：收紧间距 + 分隔线，贴合页面既有面板风格 */
/deep/ .ai-drawer .el-drawer__header {
  margin-bottom: 0;
  padding: 14px 16px;
  border-bottom: 1px solid #dcdfe6;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}
/* 抽屉内部：body 撑满剩余高度，保证底部输入区始终可见 */
/deep/ .ai-drawer .el-drawer__body {
  display: flex;
  overflow: hidden;
  padding: 12px 16px 16px;
}

/* 悬浮球：玻璃气泡流光球（呼吸 + 光晕），可拖动 */
.ai-fab {
  position: fixed;
  right: 24px;
  bottom: 96px;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none; /* 触摸拖动时不要滚动页面 */
  transition: box-shadow 0.3s;

  &:active { cursor: grabbing; }

  /* 旋转彩色光晕（Siri 的流光感） */
  &::before {
    content: '';
    position: absolute;
    top: -7px;
    right: -7px;
    bottom: -7px;
    left: -7px;
    border-radius: 50%;
    background: conic-gradient(from 0deg, #ec4899, #f97316, #22d3ee, #2563eb, #ec4899);
    filter: blur(9px);
    opacity: 0.9;
    animation: ai-swirl 6s linear infinite;
  }

  /* 呼吸扩散圈 */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 50%;
    border: 2px solid rgba(94, 176, 255, 0.75);
    animation: ai-ripple 2.8s ease-out infinite;
  }

  &.is-dragging { cursor: grabbing; }
  &.is-open { opacity: 0.7; }
}

/* 球体本体：玻璃气泡流光图标 + 呼吸缩放 */
.ai-fab-orb {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  /* 气泡本体：玻璃球 + 粉蓝无限流光（src/assets/img/ai-orb.jpg，6KB，url-loader 内联） */
  background: url(../../assets/img/ai-orb.jpg) center / cover no-repeat;
  box-shadow: 0 4px 16px rgba(79, 124, 255, 0.5);
  animation: ai-breathe 3.2s ease-in-out infinite;
}

@keyframes ai-breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.09); }
}
@keyframes ai-swirl {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes ai-ripple {
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(1.65); opacity: 0; }
}

.ai-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* 关键：允许内部滚动区收缩，否则底部输入区会被挤出可视范围 */
  width: 100%;
}
.ai-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 2px 12px;
}
.ai-empty { padding: 16px 8px; }
.ai-empty-head {
  display: flex;
  align-items: flex-start;
  margin-bottom: 18px;
  i {
    font-size: 22px;
    color: #409eff;
    margin-right: 10px;
    margin-top: 2px;
  }
}
.ai-empty-title { color: #303133; font-size: 14px; font-weight: 600; margin: 0 0 4px; }
.ai-empty-sub { color: #909399; font-size: 12px; margin: 0; line-height: 1.5; }
.ai-empty-label { color: #909399; font-size: 12px; margin: 0 0 8px; }
/* 预制问题：紧凑芯片自动换行，条数多也不挤占对话区 */
.ai-samples { display: flex; flex-wrap: wrap; margin-bottom: 2px; }
.ai-sample {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin: 0 8px 8px 0;
  padding: 6px 10px;
  background: #fafcff;
  border: 1px solid #dcdfe6;
  border-radius: 2px; /* 与页面控件圆角保持一致 */
  font-size: 13px;
  color: #409eff;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  i {
    margin-right: 6px;
    font-size: 13px;
    color: #c0c4cc;
    flex: none;
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &:hover {
    background: #ecf5ff;
    border-color: #409eff; /* 与页面输入框 hover 同色 */
    i { color: #409eff; }
  }
}

.ai-msg {
  margin-bottom: 12px;
  display: flex;
  &.is-user { justify-content: flex-end; }
}
.ai-bubble {
  max-width: 90%;
  padding: 8px 12px;
  border-radius: 2px;
  font-size: 13px;
  line-height: 1.6;
  background: #f4f6f8;
  color: #303133;
  word-break: break-all;
}
.is-user .ai-bubble { background: #409eff; color: #fff; }
.ai-text { white-space: pre-wrap; }
.ai-error { color: #f56c6c; }
.ai-chart { width: 100%; height: 220px; margin-top: 8px; }
.ai-table-wrap { margin-top: 8px; }
.ai-table-bar {
  display: flex;
  align-items: center;
  margin-top: 4px;
}
.ai-count { font-size: 12px; color: #909399; margin-right: 8px; }

.ai-tip { color: #909399; font-size: 13px; padding: 8px 0; }

/* 两组对比弹窗：一行两组输入 */
.ai-compare-row { display: flex; align-items: flex-end; }
.ai-compare-field { flex: 1; min-width: 0; }
.ai-compare-field + .ai-compare-field { margin-left: 10px; }
.ai-compare-label { font-size: 13px; color: #606266; margin-bottom: 4px; }
.ai-compare-vs { flex: none; color: #909399; font-size: 13px; padding: 0 10px 6px; }

.ai-input {
  flex: none; /* 固定高度，不参与收缩，保证始终可见 */
  border-top: 1px solid #dcdfe6;
  margin-top: 4px;
  padding-top: 12px;
}
/* 输入框圆角对齐页面控件 */
/deep/ .ai-input .el-textarea__inner {
  border-radius: 2px;
  font-size: 13px;
}
.ai-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
}
.ai-actions-right { margin-left: auto; }

.ai-saved-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f2f6fc;
}
.ai-saved-main { flex: 1; }
.ai-saved-name { font-size: 13px; color: #303133; }
.ai-saved-q { font-size: 12px; color: #909399; }
.ai-off { color: #f56c6c; margin-left: 6px; }
</style>



// WEBPACK FOOTER //
// src/components/AiQuery/index.vue